import {
  draggable,
  dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/adapter/element-adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/utils/combine';
import { attachClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/attach-closest-edge';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge/extract-closest-edge';
import type { Edge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/types';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BoardLabelEntry } from '@/entities/collab';
import { Icons } from '@/shared/ui';

import { isLabelDrag, labelDragData } from './dnd';
import styles from './WorkspaceLabels.module.css';

interface Props {
  label: BoardLabelEntry;
  reorderable: boolean;
  first: boolean;
  last: boolean;
  canManage: boolean;
  onEdit: () => void;
  onStep: (direction: 'up' | 'down') => void;
}

/**
 * One label of the catalog: drag handle, color, name, number of boards, edit action. The
 * handle is dragged with the pointer and moved with ArrowUp / ArrowDown from the keyboard; the
 * name opens the editor (a drag never counts as a click).
 */
export function LabelRow({ label, reorderable, first, last, canManage, onEdit, onStep }: Props) {
  const { t } = useTranslation();
  const ref = useRef<HTMLLIElement>(null);
  const handleRef = useRef<HTMLButtonElement>(null);
  const [dragging, setDragging] = useState(false);
  const [edge, setEdge] = useState<Edge | null>(null);

  useEffect(() => {
    const element = ref.current;
    const handle = handleRef.current;
    if (!element || !handle || !reorderable) return;
    return combine(
      draggable({
        element,
        dragHandle: handle,
        getInitialData: () => labelDragData(label.id),
        onDragStart: () => {
          setDragging(true);
        },
        onDrop: () => {
          setDragging(false);
        },
      }),
      dropTargetForElements({
        element,
        canDrop: ({ source }) => isLabelDrag(source.data) && source.data.labelId !== label.id,
        getData: ({ input }) =>
          attachClosestEdge(labelDragData(label.id), {
            element,
            input,
            allowedEdges: ['top', 'bottom'],
          }),
        onDrag: ({ self }) => {
          setEdge(extractClosestEdge(self.data));
        },
        onDragLeave: () => {
          setEdge(null);
        },
        onDrop: () => {
          setEdge(null);
        },
      }),
    );
  }, [label.id, reorderable]);

  return (
    <li
      ref={ref}
      className={[styles.row, dragging && styles.dragging, edge && styles[`drop-${edge}`]]
        .filter(Boolean)
        .join(' ')}
      data-testid="board-label"
    >
      {reorderable && (
        <button
          ref={handleRef}
          type="button"
          className={styles.handle}
          aria-label={t('workspaceLabels.dragHandle', { name: label.name })}
          title={t('workspaceLabels.dragHint')}
          onKeyDown={(event) => {
            if (event.key === 'ArrowUp' && !first) {
              event.preventDefault();
              onStep('up');
            } else if (event.key === 'ArrowDown' && !last) {
              event.preventDefault();
              onStep('down');
            }
          }}
        >
          <Icons.list />
        </button>
      )}
      <span className={styles.swatch} style={{ background: label.color }} aria-hidden />
      <button type="button" className={styles.name} onClick={onEdit}>
        <span className={styles.nameText}>{label.name}</span>
      </button>
      {label.access_controlling && (
        <span className={styles.chip} title={t('workspaceAdmin.labelControlsAccess')}>
          <Icons.key /> {t('access.grantsAccess')}
        </span>
      )}
      <span className={styles.count}>
        {t('workspaceAdmin.labelBoards', { count: label.board_count })}
      </span>
      <button
        type="button"
        className={styles.edit}
        aria-label={
          canManage
            ? t('workspaceLabels.edit', { name: label.name })
            : t('workspaceLabels.view', { name: label.name })
        }
        onClick={onEdit}
      >
        <Icons.edit />
      </button>
    </li>
  );
}
